// src/pages/calidad/PpniPncCatalogos.jsx
// Listas desplegables del F-CAL-05: agregar valores y activar o desactivar.
// Desactivar no borra nada: el valor deja de aparecer en la captura pero los
// registros que ya lo usan lo conservan.
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiDownload, FiPlus } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

const LISTAS = [
  ['producto', 'Productos'],
  ['proveedor', 'Proveedores'],
  ['area_proceso', 'Área / proceso'],
  ['tipo_desviacion', 'Tipo de desviación'],
  ['disposicion', 'Disposición'],
  ['tipo_producto', 'Tipo de producto'],
  ['cual_pcc', '¿Cuál PCC?'],
];

export default function PpniPncCatalogos() {
  const [listas, setListas] = useState(null);
  const [tipo, setTipo] = useState('producto');
  const [nuevo, setNuevo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [guardandoId, setGuardandoId] = useState(null);

  async function cargar() {
    try {
      const r = await api.get('/api/calidad/ppni-pnc/catalogos');
      setListas(r.data.data.listas);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudieron cargar las listas.' });
    }
  }
  useEffect(() => { cargar(); }, []);

  const errorTexto = (err, def) => (err.response?.status === 403 ? 'No tienes permiso de captura en Calidad.' : err.response?.data?.error || def);

  async function agregar() {
    const valor = nuevo.trim();
    if (!valor) return;
    if ((listas[tipo] || []).some((x) => x.valor.toLowerCase() === valor.toLowerCase() && x.activo)) {
      setMensaje({ tipo: 'error', texto: `"${valor}" ya está en la lista.` });
      return;
    }
    try {
      await api.post('/api/calidad/ppni-pnc/catalogos', { tipo, valor });
      setNuevo('');
      setMensaje({ tipo: 'ok', texto: `✓ "${valor}" agregado.` });
      cargar();
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudo agregar.') });
    }
  }

  async function cambiarActivo(item) {
    if (item.activo && !window.confirm(`¿Desactivar "${item.valor}"? Ya no aparecerá en la captura; los registros que ya lo tienen lo conservan.`)) return;
    try {
      setGuardandoId(item.id);
      await api.put(`/api/calidad/ppni-pnc/catalogos/${item.id}`, { activo: !item.activo });
      setListas((prev) => ({ ...prev, [item.tipo]: prev[item.tipo].map((x) => (x.id === item.id ? { ...x, activo: !x.activo } : x)) }));
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudo actualizar.') });
    } finally {
      setGuardandoId(null);
    }
  }

  const visibles = useMemo(() => {
    if (!listas) return [];
    const q = busqueda.trim().toLowerCase();
    return (listas[tipo] || [])
      .filter((x) => verInactivos || x.activo)
      .filter((x) => !q || x.valor.toLowerCase().includes(q))
      .sort((a, b) => a.valor.localeCompare(b.valor, 'es'));
  }, [listas, tipo, busqueda, verInactivos]);

  function excel() {
    const wb = XLSX.utils.book_new();
    LISTAS.forEach(([t, label]) => {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((listas[t] || []).map((x) => ({ Valor: x.valor, Activo: x.activo ? 'Sí' : 'No' }))), label.replace(/[^\wáéíóúñ ]/gi, '').slice(0, 30));
    });
    XLSX.writeFile(wb, 'Listas_F-CAL-05.xlsx');
  }

  if (!listas) return <div style={s.paginaContenedor}>{mensaje?.texto || 'Cargando...'}</div>;
  const activos = (listas[tipo] || []).filter((x) => x.activo).length;
  const inactivos = (listas[tipo] || []).length - activos;

  return (
    <div style={{ ...s.paginaContenedor, maxWidth: 820 }} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Listas del F-CAL-05 (PPNI y PNC)</h1>
          <div style={{ fontSize: 12, color: '#8A7690' }}>Opciones de las listas desplegables de la captura.</div>
        </div>
        <button onClick={excel} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Excel</button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {LISTAS.map(([t, label]) => (
          <button key={t} onClick={() => { setTipo(t); setBusqueda(''); }} style={{ ...s.tabBtn, ...(tipo === t ? s.tabBtnActivo : {}) }}>
            {label} <span style={{ opacity: 0.6 }}>({(listas[t] || []).filter((x) => x.activo).length})</span>
          </button>
        ))}
      </div>

      <div style={s.tarjeta}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <input type="text" placeholder={`Agregar a ${LISTAS.find((l) => l[0] === tipo)[1].toLowerCase()}…`} value={nuevo}
            onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') agregar(); }}
            style={{ ...s.filtroInput, flex: 1, minWidth: 220 }} maxLength={200} />
          <button onClick={agregar} style={{ ...s.botonBuscar2, display: 'flex', alignItems: 'center', gap: 6 }}><FiPlus /> Agregar</button>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input type="text" placeholder="Buscar…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} style={{ ...s.filtroInput, minWidth: 200 }} />
          {inactivos > 0 && (
            <label style={{ fontSize: 12, color: '#6E4F80', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} /> Ver inactivos ({inactivos})
            </label>
          )}
          <span style={{ fontSize: 12, color: '#8A7690' }}>{activos} activos</span>
        </div>

        {visibles.length === 0 && <div style={{ fontSize: 12, color: '#8A7690' }}>Sin valores.</div>}
        {visibles.map((item) => (
          <div key={item.id} style={{ ...x.fila, opacity: item.activo ? 1 : 0.55 }}>
            <span style={{ fontSize: 13, color: '#2A1830' }}>{item.valor}</span>
            <button onClick={() => cambiarActivo(item)} disabled={guardandoId === item.id} style={item.activo ? x.botonDesactivar : x.botonActivar}>
              {guardandoId === item.id ? '...' : item.activo ? 'Desactivar' : 'Activar'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const x = {
  fila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F0E9E0' },
  botonDesactivar: { padding: '4px 10px', fontSize: 11, color: '#8A7690', background: 'transparent', border: '1px solid #E6DDD2', borderRadius: 6, cursor: 'pointer' },
  botonActivar: { padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#3E7C5A', background: '#F0F7F1', border: '1px solid #BFDCC9', borderRadius: 6, cursor: 'pointer' },
};